import * as React from 'react'
import { useMemo, useState } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Tooltip,
} from '@fluentui/react-components'
import { AddFilled, AddRegular, ArrowDownloadFilled, ArrowDownloadRegular, bundleIcon } from '@fluentui/react-icons'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role } from '../../hooks/useRoles'
import { useTranslation } from 'react-i18next'
import { FileStatus, IOverviewItemDto } from '../../config/fileConfig'
import useUser from '../../hooks/useUser'
import { useHistory } from 'react-router-dom'
import UserPicker from '../../components/TagPicker/UserPicker'
import FileStatusPicker, { FileStatusLabels } from '../../components/TagPicker/FileStatusPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import useSubmitDocumentGenerationRequest from '../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import { useSettings } from '../../hooks/useSettings'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, SearchHintIcon } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import { UserStatus } from '../../config/userConfig'
import { FileListItem } from './FileListItem'
import PartyPicker from '../../components/TagPicker/PartyPicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'

export const useItemStyles = makeStyles({
    listFileItem: {
        display: 'grid',
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) auto var(--baseOverviewIconWidth) var(--baseOverviewFileWidth) var(--baseOverviewDateWidth)',
    },
    listFileItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto var(--baseOverviewDateWidth)',
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)

export type FileOverviewProps = BaseOverviewProps<IOverviewItemDto, FileFilterType, FileSortingType>

export type BaseFileOverviewProps = Partial<FileOverviewProps> & {
    baseUrl?: string

    crmRelationId?: string
}

export type BaseFileOverviewLineItemProps = BaseFileOverviewProps &
    BaseOverviewLineItemProps<IOverviewItemDto, FileFilterType, FileSortingType> & {
        listItemSmall: boolean
    }

export type BaseFileOverviewFilterProps = BaseOverviewFilterProps<IOverviewItemDto, FileFilterType, FileSortingType> & BaseFileOverviewProps

export type FileFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        statuses?: FileStatus[]
        ownerUsers?: string[]
        executiveUsers?: string[]
        parties?: string[] //used by PartyListItem
        crmRelations?: string[] //used by CRMFileOverview
        kyc?: boolean
    }

type FileSorting = {
    id: string
    name: string
    reference: string
    'history.updated': string
}

export type FileSortingType = BaseOverviewSortingType<FileSorting>

export function FileFilter(props: BaseFileOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<FileFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const orientation = useMemo(() => (props.viewportMedium ? 'vertical' : 'horizontal'), [props.viewportMedium])

    return (
        <>
            <Field label={translate('file.filter.owner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.ownerUsers || []}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('ownerUsers', selectedOptions)}
                />
            </Field>
            <Field label={translate('file.filter.executive')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.executiveUsers || []}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('executiveUsers', selectedOptions)}
                />
            </Field>
            {!props.crmRelationId && modules.has(ModuleType.CRM) && (
                <Field label={translate('file.filter.crmrelation')} orientation={orientation}>
                    <PartyPicker
                        selectedOptions={values.crmRelations || []}
                        onOptionsSelect={(_, selectedOptions) => setFieldValue('crmRelations', selectedOptions)}
                        params={{ crmRelation: true }}
                    />
                </Field>
            )}
            {!props.crmRelationId && modules.has(ModuleType.CRM) && (
                <Field label={translate('file.filter.kyc')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.kyc?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) => setFieldValue('kyc', data.checked === 'mixed' ? undefined : data.checked)}
                    />
                </Field>
            )}
            <Field label={translate('file.status.label')} orientation={orientation}>
                <FileStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('statuses', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('file.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const headerSortingItems: FileSortingType[] = [
    {
        property: ['name', 'reference'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.az',
    },
    {
        property: ['name', 'reference'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.za',
    },
    {
        property: ['reference'],
        direction: SortDirection.Ascending,
    },
    {
        property: ['reference'],
        direction: SortDirection.Descending,
    },
    {
        property: ['history.updated', 'reference'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.lastaccessasc',
    },
    {
        property: ['history.updated', 'reference'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.lastaccessdesc',
        default: true,
    },
]

export function HeaderActionsButtons(props: BaseFileOverviewFilterProps) {
    const history = useHistory()
    const roles = useRoles()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<FileFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<FileFilterType>(DocumentGenerationType.FileXls)

    return (
        <>
            {/*Only hide when not in CRMFileOverview. */}
            {roles.none([Role.FileLimited]) &&
                (props.crmRelationId !== undefined || settings.crmNoDefaultFile === undefined || settings.crmNoDefaultFile === false) && (
                    <Tooltip withArrow relationship={'label'} content={translate('action.create.file.title')}>
                        <Button
                            icon={<AddIcon />}
                            appearance={'primary'}
                            onClick={() => history.push('/file/add', { crmRelationId: props.crmRelationId, returnBaseUrl: props.baseUrl })}
                        />
                    </Tooltip>
                )}
            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
        </>
    )
}

function HeaderFilterTags() {
    const { values, setValues } = useFormikContext<FileFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()

    return (
        <div className={overviewStyles.flexGapXS}>
            <InteractionTag
                selected={values.statuses?.includes(FileStatus.Open)}
                onClick={() =>
                    setValues({
                        ...values,
                        statuses: values.statuses?.includes(FileStatus.Open)
                            ? values.statuses.filter(value => value !== FileStatus.Open)
                            : [...values.statuses, FileStatus.Open],
                        tag: undefined,
                    })
                }
            >
                <InteractionTagPrimary>{translate(FileStatusLabels.get(FileStatus.Open).name)}</InteractionTagPrimary>
            </InteractionTag>
            <InteractionTag
                selected={values.statuses?.includes(FileStatus.Archived)}
                onClick={() =>
                    setValues({
                        ...values,
                        statuses: values.statuses?.includes(FileStatus.Archived)
                            ? values.statuses.filter(value => value !== FileStatus.Archived)
                            : [...values.statuses, FileStatus.Archived],
                        tag: undefined,
                    })
                }
            >
                <InteractionTagPrimary>{translate(FileStatusLabels.get(FileStatus.Archived).name)}</InteractionTagPrimary>
            </InteractionTag>
        </div>
    )
}

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} withArrow>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

export default function BaseFileOverview(props: BaseFileOverviewProps) {
    const axios = useAxios()
    const history = useHistory()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { settings } = useSettings()
    const { currentUser } = useUser()

    const defaultFilterValues = useMemo<FileFilterType>(
        () => ({
            ownerUsers: currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            executiveUsers: currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            crmRelations: [],
            statuses: [FileStatus.Open],
            attributes: [],
            query: '',
            ...props?.fixedFilterValues,
            ...props?.defaultFilterValues,
        }),
        [settings, props],
    )

    const countItems = (_column?: number, filterValues?: FileFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/file/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        _column?: number,
        filterValues?: FileFilterType,
        sortingValues?: FileSortingType,
        start?: number,
        size?: number,
    ): Promise<IOverviewItemDto[]> => {
        if ((sortingValues?.property?.[0] as string) === 'lastAccess') {
            sortingValues = { ...sortingValues, property: ['history.updated', 'id'] }
        }

        return axios
            .get('/file', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<IOverviewItemDto[]>) => response?.data || [])
    }

    if (!settings) {
        return <></>
    }

    return (
        <BaseOverview<IOverviewItemDto, FileFilterType, FileSortingType>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={(lineProps: BaseFileOverviewLineItemProps) => <FileListItem {...lineProps} />}
            listItemClassnames={(lineProps: BaseFileOverviewLineItemProps) => {
                return [itemStyles.listFileItem, lineProps.viewportNarrow && itemStyles.listFileItemSmall]
            }}
            onItemClicked={(lineProps: BaseFileOverviewLineItemProps) => history.push(`/file/${lineProps.item.id}`)}
            headerDefaultFilterTags={HeaderFilterTags}
            headerActionsButtons={HeaderActionsButtons}
            headerSortingItems={headerSortingItems}
            filterContent={FileFilter}
            filterInputHint={FilterInputHint}
            inputPlaceholderKey={'file.filter.search'}
            {...props}
        />
    )
}
